import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Brain, Activity, Zap, ArrowRight, CheckCircle2, TrendingUp } from "lucide-react";

const HomePage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const services = [
    {
      icon: <Brain size={32} />,
      title: "AI-Driven Digital Marketing Strategy",
      description: "Strategic planning using data intelligence, audience insights, channel prioritization, and AI-assisted decision making. We turn marketing noise into strategic growth signals.",
      span: "md:col-span-2"
    },
    {
      icon: <Activity size={32} />,
      title: "Brand Communication Strategy",
      description: "Crafting brand narratives, messaging frameworks, tone of voice, and content systems that create trust and recall.",
      span: ""
    },
    {
      icon: <Zap size={32} />,
      title: "AI Automation for Marketing Operations",
      description: "Automating workflows, reporting, content systems, CRM touchpoints, and performance insights to scale efficiently.",
      span: ""
    }
  ];

  const trustMetrics = [
    { value: "50+", label: "Brands Partnered" },
    { value: "3.5x", label: "Avg. Growth Rate" },
    { value: "AI-First", label: "Strategy Approach" }
  ];

  const benefits = [
    "Strategy-first, not tactics-first approach",
    "AI-powered insights, human-led execution",
    "Founder-led partnership and accountability",
    "Long-term growth, not vanity metrics"
  ];

  return (
    <div className="min-h-screen noise-texture">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-32 pb-20">
        {/* Background Image with Gradient Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1647356191320-d7a1f80ca777?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Abstract network visualization"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-darkBg via-darkBg/95 to-brandPrimary/10"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
          {/* Left Content - 60% */}
          <motion.div {...fadeInUp} className="lg:col-span-3 space-y-8">
            <div className="inline-block">
              <span className="text-brandSecondary font-medium text-sm uppercase tracking-wider" data-testid="hero-label">
                Strategic AI Growth Partner
              </span>
            </div>
            
            <h1 className="font-heading font-bold text-5xl sm:text-6xl lg:text-7xl leading-tight" data-testid="hero-headline">
              <span className="text-gradient">Data Points.</span>
              <br />
              <span className="text-gradient">Touch Points.</span>
              <br />
              <span className="text-gradient">Turning Points.</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-2xl leading-relaxed" data-testid="hero-subheadline">
              We turn marketing noise into strategic growth signals for ambitious brands. AI-powered insights meet founder-led execution.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link
                to="/contact"
                data-testid="hero-primary-cta"
                className="inline-flex items-center justify-center gap-2 bg-brandPrimary hover:bg-brandPrimary/90 text-white px-8 py-4 rounded-full font-medium text-lg shadow-lg hover:shadow-brandPrimary/25 transition-all group"
              >
                Book a Free Strategy Call
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/case-studies"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white/20 text-white hover:bg-white/5 hover:border-white/40 px-8 py-4 rounded-full font-medium text-lg transition-all"
              >
                See How We Work
              </Link>
            </div>

            {/* Trust Metrics */}
            <div className="flex flex-wrap gap-8 pt-8">
              {trustMetrics.map((metric, idx) => (
                <div key={idx} data-testid={`trust-metric-${idx}`}>
                  <div className="font-heading font-bold text-3xl text-brandPrimary">{metric.value}</div>
                  <div className="text-sm text-gray-400 mt-1">{metric.label}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right Visual - 40% */}
          <motion.div
            {...fadeInUp}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="lg:col-span-2 relative"
          >
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/17485657/pexels-photo-17485657.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="AI visualization"
                className="w-full rounded-3xl shadow-2xl animate-float"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-darkBg via-transparent to-transparent rounded-3xl"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Bento Grid */}
      <section className="py-32 px-6" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mb-4" data-testid="services-heading">
              How We Drive Growth
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto" data-testid="services-subheading">
              Three pillars of strategic advantage for modern brands
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                data-testid={`service-card-${idx}`}
                className={`group relative overflow-hidden rounded-3xl glass-card hover:border-brandPrimary/50 transition-all duration-500 p-8 ${service.span}`}
              >
                <div className="relative z-10">
                  <div className="text-brandPrimary mb-4">
                    {service.icon}
                  </div>
                  <h3 className="font-heading font-semibold text-2xl text-white mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {service.description}
                  </p>
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-brandPrimary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-32 px-6 bg-darkPaper/50" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mb-6" data-testid="why-choose-heading">
                Why Founders Choose Us
              </h2>
              <p className="text-gray-400 text-lg mb-8" data-testid="why-choose-description">
                We're not another agency pushing deliverables. We're strategic partners who understand that growth comes from clarity, not chaos.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} data-testid={`benefit-item-${idx}`} className="flex items-start gap-3">
                    <CheckCircle2 size={24} className="text-brandSecondary flex-shrink-0 mt-1" />
                    <span className="text-gray-300">{benefit}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/about"
                data-testid="why-choose-cta"
                className="inline-flex items-center gap-2 text-brandPrimary hover:text-brandPrimary/80 font-medium mt-8 group"
              >
                Learn Our Story
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1636755393526-a2249074de99?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Strategic growth visualization"
                className="rounded-3xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-darkBg via-transparent to-transparent rounded-3xl"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-3xl p-12 md:p-16"
          >
            <TrendingUp size={48} className="mx-auto text-brandPrimary mb-6" />
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mb-6" data-testid="cta-heading">
              Ready to Turn Data into Growth?
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto" data-testid="cta-description">
              Let's discuss how AI-driven strategy can transform your marketing operations and accelerate growth.
            </p>
            <Link
              to="/contact"
              data-testid="cta-button"
              className="inline-flex items-center gap-2 bg-brandPrimary hover:bg-brandPrimary/90 text-white px-10 py-5 rounded-full font-medium text-lg shadow-lg hover:shadow-brandPrimary/25 transition-all"
            >
              Book Your Free Strategy Call
              <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
